import { create } from "zustand";
import {
  CriteriaResponse,
  ValidationQuestion,
  AIScore,
  QuestionAnswer,
  AnswerSource
} from "@/zapi-frontend/shared/const/validation-questions";
import { toast } from "sonner";
import $api from "../shared/api/axios";

type TValidationStore = {
  questionsCache: Record<string, ValidationQuestion[]>;
  isFetchingQuestions: boolean;
  isValidating: boolean;
  currentlyValidating: string | null;
  validatingChapterIds: Set<number>;
  questions: ValidationQuestion[];
  setQuestions: (questions: ValidationQuestion[]) => void;
  setIsFetchingQuestions: (isFetchingQuestions: boolean) => void;
  setIsValidating: (isValidating: boolean) => void;
  setCurrentlyValidating: (slug: string | null) => void;
  updateQuestionAnswer: (
    slug: string,
    answer: QuestionAnswer,
    explanation?: string,
    answerSource?: AnswerSource,
    projectId?: string,
    chapterId?: number
  ) => Promise<void>;
  submitQuestionAnswer: (
    projectId: string,
    chapterId: number,
    slug: string,
    answer: QuestionAnswer,
    answerSource?: AnswerSource
  ) => Promise<void>;
  validateAllQuestions: (projectId: string, chapterId: number) => Promise<void>;
  fetchQuestions: (projectId: string, chapterId: number) => Promise<void>;
  setChapterCurrent: (projectId: string, chapterId: number) => Promise<void>;
  addValidatingChapter: (id: number) => void;
  removeValidatingChapter: (id: number) => void;
  getQuestionsForChapter: (projectId: string, chapterId: number) => ValidationQuestion[];
};

export const useValidationStore = create<TValidationStore>((set, get) => ({
  questionsCache: {},
  questions: [],
  isFetchingQuestions: false,
  isValidating: false,
  currentlyValidating: null,
  validatingChapterIds: new Set(),
  setQuestions: (questions) => set({ questions }),
  setIsFetchingQuestions: (isFetchingQuestions) => set({ isFetchingQuestions }),
  setIsValidating: (isValidating) => set({ isValidating }),

  setCurrentlyValidating: (slug) => set({ currentlyValidating: slug }),

  updateQuestionAnswer: async (slug, answer, explanation, answerSource = "user", projectId, chapterId) => {
    const exp = explanation ?? null;

    if (projectId && chapterId) {
      const cacheKey = `${projectId}-${chapterId}`;
      set((state) => {
        const cachedQuestions = state.questionsCache[cacheKey];
        if (!cachedQuestions) return state;

        const cachedQuestion = cachedQuestions.find((q) => q.slug === slug);
        if (!cachedQuestion) return state;

        const updatedQuestion = { ...cachedQuestion, answer, explanation: exp, answerSource };
        return {
          questionsCache: {
            ...state.questionsCache,
            [cacheKey]: cachedQuestions.map((q) => (q.slug === slug ? updatedQuestion : q))
          }
        };
      });
    }

    const question = get().questions.find((q) => q.slug === slug);
    if (!question) return;

    const updatedQuestion = { ...question, answer, explanation: exp, answerSource };
    set((state) => ({
      questions: state.questions.map((q) => (q.slug === slug ? updatedQuestion : q))
    }));
  },

  submitQuestionAnswer: async (
    projectId: string,
    chapterId: number,
    slug: string,
    answer: QuestionAnswer,
    answerSource?: AnswerSource
  ) => {
    const question = get().questions.find((question) => question.slug === slug);
    if (!question) return;

    if (question.answer === answer && question.answerSource === answerSource) {
      return;
    }

    const previousAnswer = question.answer;
    const previousSource = question.answerSource;
    get().updateQuestionAnswer(slug, answer, undefined, answerSource, projectId, chapterId);
    set({ currentlyValidating: slug });
    try {
      await $api.put<{ data: AIScore }>(`/projects/${projectId}/chapters/${chapterId}/criteria`, {
        criterionSlug: slug,
        answer
      });
    } catch (error) {
      get().updateQuestionAnswer(slug, previousAnswer, undefined, previousSource, projectId, chapterId);
      console.error("Validation error:", error);
    }
    set({ currentlyValidating: null });
    toast.success("Question answer updated successfully");
  },
  addValidatingChapter: (id) =>
    set((state) => {
      const next = new Set(state.validatingChapterIds);
      next.add(id);
      return { validatingChapterIds: next };
    }),
  removeValidatingChapter: (id) =>
    set((state) => {
      const next = new Set(state.validatingChapterIds);
      next.delete(id);
      return { validatingChapterIds: next };
    }),
  validateAllQuestions: async (projectId: string, chapterId: number) => {
    const {
      validatingChapterIds,
      questions,
      addValidatingChapter,
      removeValidatingChapter,
      setChapterCurrent,
      fetchQuestions,
      updateQuestionAnswer
    } = get();
    if (validatingChapterIds.has(chapterId)) return;

    addValidatingChapter(chapterId);
    set({ isValidating: true });

    toast.info("Starting validation of all criteria...");

    for (const question of questions) {
      try {
        set({ currentlyValidating: question.slug });
        const response = await $api.post<{ data: AIScore }>(
          `/projects/${projectId}/chapters/${chapterId}/criteria/score-with-ai`,
          { criterionSlug: question.slug }
        );
        const answer = response.data.data.status;
        const explanation = response.data.data.explanation;
        updateQuestionAnswer(question.slug, answer, explanation, "ai", projectId, chapterId);
      } catch (error) {
        console.error("Validation error:", error);
        updateQuestionAnswer(question.slug, "no", "Validation failed", "ai", projectId, chapterId);
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    await setChapterCurrent(projectId, chapterId);
    await fetchQuestions(projectId, chapterId);
    removeValidatingChapter(chapterId);
    set({ isValidating: false, currentlyValidating: null });
    toast.success(`Validation complete!`);
  },

  fetchQuestions: async (projectId: string, chapterId: number) => {
    const cacheKey = `${projectId}-${chapterId}`;
    const { questionsCache } = get();

    if (questionsCache[cacheKey]) {
      set({ questions: questionsCache[cacheKey], isFetchingQuestions: false });
      return;
    }

    set({ isFetchingQuestions: true });
    try {
      const response = await $api.get<{ data: CriteriaResponse }>(
        `/projects/${projectId}/chapters/${chapterId}/criteria`
      );
      const newQuestions = response.data.data.map((question) => ({
        ...question,
        answerSource: question.setBy ?? "user"
      }));

      set((state) => ({
        questionsCache: { ...state.questionsCache, [cacheKey]: newQuestions },
        questions: newQuestions,
        isFetchingQuestions: false
      }));
    } catch (error) {
      set({ isFetchingQuestions: false });
      return;
    }
  },

  getQuestionsForChapter: (projectId: string, chapterId: number) => {
    const cacheKey = `${projectId}-${chapterId}`;
    const { questionsCache } = get();
    return questionsCache[cacheKey] || [];
  },

  setChapterCurrent: async (projectId: string, chapterId: number) => {
    try {
      await $api.patch(`/projects/${projectId}/chapters/${chapterId}/set-current`, {});
    } catch (error) {
      console.error("Error setting chapter as current:", error);
    }
  }
}));
